/**
 * Компактный тулбар карты тактики: основная полоса (nav/draw) + выпадающие CAD/Анализ.
 * Не растягивается на всю ширину карты; сворачивается в полоску.
 */
import { useState, type ComponentType, type ReactNode } from "react";
import {
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  Circle,
  CloudOff,
  Combine,
  Hand,
  Hexagon,
  Lasso,
  Magnet,
  Mountain,
  MousePointer2,
  PenLine,
  Pencil,
  Radar,
  Redo2,
  RotateCw,
  Ruler,
  Scan,
  Scissors,
  SquareMinus,
  Trash2,
  Type,
  Undo2,
  Users,
  Waypoints,
  Wrench,
} from "lucide-react";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export type TacticalToolbarMode =
  | "pan"
  | "select"
  | "point"
  | "line"
  | "freehand"
  | "polygon"
  | "vertex"
  | "scissors"
  | "rotate"
  | "lasso"
  | "text";

type ToolDef = {
  mode: TacticalToolbarMode;
  Icon: ComponentType<{ className?: string }>;
  title: string;
};

const NAV_TOOLS: ToolDef[] = [
  { mode: "pan", Icon: Hand, title: "Панорама (H)" },
  { mode: "select", Icon: MousePointer2, title: "Выбор (V/S)" },
];

const DRAW_TOOLS: ToolDef[] = [
  { mode: "point", Icon: Circle, title: "Точка (P)" },
  { mode: "line", Icon: Pencil, title: "Линия (L)" },
  { mode: "freehand", Icon: PenLine, title: "Свободный штрих (F)" },
  { mode: "polygon", Icon: Hexagon, title: "Полигон (G)" },
  { mode: "text", Icon: Type, title: "Текст (T)" },
];

const CAD_MODE_TOOLS: ToolDef[] = [
  { mode: "vertex", Icon: Waypoints, title: "Правка вершин" },
  { mode: "scissors", Icon: Scissors, title: "Ножницы (разрез)" },
  { mode: "rotate", Icon: RotateCw, title: "Поворот (R)" },
  { mode: "lasso", Icon: Lasso, title: "Лассо (W)" },
];

const CAD_MODE_SET = new Set<TacticalToolbarMode>(
  CAD_MODE_TOOLS.map((t) => t.mode),
);

const STORAGE_KEY = "gpml.tactical.toolbar.collapsed";

/** Выше chrome карты (z-220), ниже Sheet/Dialog help (z-300). */
const MENU_Z = "z-[240]";

function readCollapsed(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function ToolbarRow({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-0.5">{children}</div>;
}

function Sep() {
  return <span className="mx-0.5 hidden h-6 w-px bg-border sm:inline-block" aria-hidden />;
}

type Props = {
  toolMode: TacticalToolbarMode;
  canManage: boolean;
  cadBooleanMode: "union" | "difference" | null;
  snapEnabled: boolean;
  snapToleranceM: number;
  snapToMvt: boolean;
  view3d: boolean;
  losActive: boolean;
  viewshedActive: boolean;
  measureActive: boolean;
  canUndo: boolean;
  canRedo: boolean;
  selectedCount: number;
  collabConnected: boolean;
  presenceCount: number;
  isOnline: boolean;
  pendingMutations: number;
  fromCache: boolean;
  unionDisabled: boolean;
  differenceDisabled: boolean;
  onToolMode: (mode: TacticalToolbarMode) => void;
  onToggleUnion: () => void;
  onToggleDifference: () => void;
  onToggleSnap: () => void;
  onToggle3d: () => void;
  onToggleLos: () => void;
  onToggleViewshed: () => void;
  onToggleMeasure: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onDeleteSelected: () => void;
};

export function TacticalMapToolbar({
  toolMode,
  canManage,
  cadBooleanMode,
  snapEnabled,
  snapToleranceM,
  snapToMvt,
  view3d,
  losActive,
  viewshedActive,
  measureActive,
  canUndo,
  canRedo,
  selectedCount,
  collabConnected,
  presenceCount,
  isOnline,
  pendingMutations,
  fromCache,
  unionDisabled,
  differenceDisabled,
  onToolMode,
  onToggleUnion,
  onToggleDifference,
  onToggleSnap,
  onToggle3d,
  onToggleLos,
  onToggleViewshed,
  onToggleMeasure,
  onUndo,
  onRedo,
  onDeleteSelected,
}: Props) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const setCollapsedPersist = (next: boolean) => {
    setCollapsed(next);
    try {
      sessionStorage.setItem(STORAGE_KEY, next ? "1" : "0");
    } catch {
      /* ignore */
    }
  };

  const cadActive =
    CAD_MODE_SET.has(toolMode) || cadBooleanMode != null || snapEnabled;
  const analysisActive = view3d || losActive || viewshedActive || measureActive;

  const activeCadTool = CAD_MODE_TOOLS.find((t) => t.mode === toolMode);
  const CadTriggerIcon = activeCadTool?.Icon ?? Wrench;

  const toolBtn = (t: ToolDef) => (
    <Button
      key={t.mode}
      type="button"
      size="icon"
      variant={toolMode === t.mode ? "default" : "ghost"}
      className="h-8 w-8 shrink-0"
      title={t.title}
      aria-label={t.title}
      disabled={!canManage && t.mode !== "pan" && t.mode !== "select"}
      onClick={() => onToolMode(t.mode)}
    >
      <t.Icon className="h-4 w-4" />
    </Button>
  );

  if (collapsed) {
    return (
      <div className="pointer-events-auto absolute left-3 top-3 z-[220] flex flex-col items-center gap-1 rounded-md border bg-card/95 p-1 shadow-md backdrop-blur">
        <Button
          type="button"
          size="icon"
          variant="secondary"
          className="h-8 w-8"
          aria-label="Развернуть тулбар"
          title="Развернуть тулбар"
          onClick={() => setCollapsedPersist(false)}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
        <span
          className="px-0.5 pb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
          style={{ writingMode: "vertical-rl" }}
        >
          Инструменты
        </span>
      </div>
    );
  }

  return (
    <div className="pointer-events-auto absolute left-3 top-3 z-[220] flex w-max max-w-[min(calc(100%-1.5rem),22rem)] flex-col gap-1 rounded-md border bg-card/95 p-1.5 shadow-md backdrop-blur">
      <ToolbarRow>
        <SectionHelpTooltip label="Справка: тулбар карты">
          <p>Рисование на верхней полосе; CAD и анализ — в меню ниже.</p>
          <ul className="mt-1 list-disc space-y-1 pl-4">
            <li>Хоткеи: H pan, V/S select, P точка, L линия, G полигон, T текст, F freehand, R поворот, W лассо</li>
            <li>Ctrl/Cmd+Z / Y — undo/redo; Esc — сброс режима</li>
            <li>«‹» сворачивает тулбар в полоску</li>
          </ul>
        </SectionHelpTooltip>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8 shrink-0"
          aria-label="Свернуть тулбар"
          title="Свернуть"
          onClick={() => setCollapsedPersist(true)}
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Sep />
        {NAV_TOOLS.map(toolBtn)}
        <Sep />
        {DRAW_TOOLS.map(toolBtn)}
      </ToolbarRow>

      <ToolbarRow>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              size="sm"
              variant={cadActive ? "default" : "outline"}
              className="h-8 gap-1 px-2 text-xs"
              aria-label="Меню CAD"
              title="CAD-инструменты"
            >
              <CadTriggerIcon className="h-3.5 w-3.5" />
              CAD
              <ChevronDown className="h-3 w-3 opacity-70" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className={cn(MENU_Z, "min-w-[14rem]")}>
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Правка геометрии
            </DropdownMenuLabel>
            {CAD_MODE_TOOLS.map((t) => (
              <DropdownMenuItem
                key={t.mode}
                disabled={!canManage}
                className="gap-2 text-xs"
                onSelect={() => onToolMode(t.mode)}
              >
                <t.Icon className="h-3.5 w-3.5" />
                <span className="flex-1">{t.title}</span>
                {toolMode === t.mode ? (
                  <span className="text-[10px] text-muted-foreground">вкл</span>
                ) : null}
              </DropdownMenuItem>
            ))}
            {canManage && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  Булевы операции
                </DropdownMenuLabel>
                <DropdownMenuItem
                  disabled={unionDisabled}
                  className="gap-2 text-xs"
                  onSelect={onToggleUnion}
                >
                  <Combine className="h-3.5 w-3.5" />
                  Объединить полигоны
                  {cadBooleanMode === "union" ? (
                    <span className="ml-auto text-[10px] text-muted-foreground">вкл</span>
                  ) : null}
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={differenceDisabled}
                  className="gap-2 text-xs"
                  onSelect={onToggleDifference}
                >
                  <SquareMinus className="h-3.5 w-3.5" />
                  Вычесть полигон
                  {cadBooleanMode === "difference" ? (
                    <span className="ml-auto text-[10px] text-muted-foreground">вкл</span>
                  ) : null}
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="gap-2 text-xs" onSelect={onToggleSnap}>
              <Magnet className="h-3.5 w-3.5" />
              <span className="flex-1">
                Snap
                {snapEnabled
                  ? ` (${snapToleranceM} м${snapToMvt ? ", +MVT" : ""})`
                  : ""}
              </span>
              {snapEnabled ? (
                <span className="text-[10px] text-muted-foreground">вкл</span>
              ) : null}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              size="sm"
              variant={analysisActive ? "default" : "outline"}
              className="h-8 gap-1 px-2 text-xs"
              aria-label="Меню анализа"
              title="Анализ на карте"
            >
              {viewshedActive ? (
                <Radar className="h-3.5 w-3.5" />
              ) : losActive ? (
                <Scan className="h-3.5 w-3.5" />
              ) : measureActive ? (
                <Ruler className="h-3.5 w-3.5" />
              ) : view3d ? (
                <Mountain className="h-3.5 w-3.5" />
              ) : (
                <Scan className="h-3.5 w-3.5" />
              )}
              Анализ
              <ChevronDown className="h-3 w-3 opacity-70" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className={cn(MENU_Z, "min-w-[14rem]")}>
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Обзор и измерение
            </DropdownMenuLabel>
            <DropdownMenuItem className="gap-2 text-xs" onSelect={onToggle3d}>
              <Mountain className="h-3.5 w-3.5" />
              <span className="flex-1">3D-обзор</span>
              {view3d ? (
                <span className="text-[10px] text-muted-foreground">вкл</span>
              ) : null}
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2 text-xs" onSelect={onToggleLos}>
              <Scan className="h-3.5 w-3.5" />
              <span className="flex-1">Прямая видимость (LOS)</span>
              {losActive ? (
                <span className="text-[10px] text-muted-foreground">вкл</span>
              ) : null}
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2 text-xs" onSelect={onToggleViewshed}>
              <Radar className="h-3.5 w-3.5" />
              <span className="flex-1">Зона видимости</span>
              {viewshedActive ? (
                <span className="text-[10px] text-muted-foreground">вкл</span>
              ) : null}
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2 text-xs" onSelect={onToggleMeasure}>
              <Ruler className="h-3.5 w-3.5" />
              <span className="flex-1">Измерение</span>
              {measureActive ? (
                <span className="text-[10px] text-muted-foreground">вкл</span>
              ) : null}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {canManage && (
          <>
            <Sep />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-8 w-8 shrink-0"
              title="Отменить (Ctrl+Z)"
              aria-label="Отменить последнее действие"
              disabled={!canUndo}
              onClick={onUndo}
            >
              <Undo2 className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-8 w-8 shrink-0"
              title="Повторить (Ctrl+Y)"
              aria-label="Повторить отменённое действие"
              disabled={!canRedo}
              onClick={onRedo}
            >
              <Redo2 className="h-4 w-4" />
            </Button>
          </>
        )}
        {canManage && selectedCount > 0 && (
          <Button
            type="button"
            size="icon"
            variant="destructive"
            className="h-8 w-8 shrink-0"
            title={
              selectedCount > 1 ? `Удалить выбранные (${selectedCount})` : "Удалить"
            }
            aria-label="Удалить выбранные"
            onClick={onDeleteSelected}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
        <Sep />
        <span
          className={cn(
            "inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs",
            collabConnected
              ? "bg-muted text-foreground"
              : "bg-muted/60 text-muted-foreground",
          )}
          title={
            collabConnected
              ? `На доске: ${presenceCount || 1}`
              : "Realtime отключён — переподключение…"
          }
        >
          <Users className="h-3.5 w-3.5" />
          {collabConnected ? presenceCount || 1 : "…"}
        </span>
        {(!isOnline || pendingMutations > 0 || fromCache) && (
          <span
            className={cn(
              "inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs",
              !isOnline
                ? "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100"
                : "bg-muted text-foreground",
            )}
            title={
              !isOnline
                ? `Нет сети${pendingMutations ? ` · в очереди ${pendingMutations}` : ""}${fromCache ? " · кэш" : ""}`
                : `Ожидают sync: ${pendingMutations}`
            }
          >
            <CloudOff className="h-3.5 w-3.5" />
            {!isOnline ? "offline" : "sync"}
            {pendingMutations > 0 ? ` ${pendingMutations}` : ""}
          </span>
        )}
      </ToolbarRow>
    </div>
  );
}
